import { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Upload, FileText, Loader2, AlertCircle, Info, X, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveStatusId } from '@/hooks/useDefaultStatus';
import { getDefaultStatusId } from '@/hooks/useDefaultStatus';
import { profileService, vendorService, attachmentService } from '@/services';
import { quoteService } from '@/services';
import {
  QuoteExtractionResults,
  QuoteExtractionHeader,
  type QuoteExtractionResult,
  type QuoteLineItem,
} from '@/components/quote/QuoteExtractionResults';
import { UploadQuoteSupportingDocs } from '@/components/quote/UploadQuoteSupportingDocs';
import { ResponsiveSplit } from '@/components/layout/ResponsiveSplit';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { ServiceContext } from '@prolarus/shared';

interface Vendor {
  id: string;
  name: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
const ACCEPTED_EXTENSIONS = '.pdf,.xlsx,.xls';

export default function UploadQuote() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [results, setResults] = useState<QuoteExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [emailText, setEmailText] = useState('');
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  // Detect if we're adding a version to an existing quote
  const [searchParams] = useSearchParams();
  const quoteIdParam = searchParams.get('quoteId');
  const isAddingVersion = !!quoteIdParam;

  // Header fields (editable)
  const [headerData, setHeaderData] = useState({
    well_name: '',
    well_api: '',
    vendor_quote_number: '',
    effective_date: '',
    expiration_date: '',
    vendor_notes: '',
  });
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const { data: activeVendorStatus } = useActiveStatusId('vendor');

  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['vendors', profile?.company_id, activeVendorStatus?.id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await vendorService.listActive(profile.company_id, activeVendorStatus?.id || null);
      if (result.error) throw result.error;
      return (result.data || []).map((v) => ({ id: v.id, name: v.name }));
    },
    enabled: !!profile?.company_id,
  });

  // Fetch existing quote when adding version
  const { data: existingQuote, isLoading: isLoadingQuote } = useQuery({
    queryKey: ['quote', quoteIdParam],
    queryFn: async () => {
      const result = await quoteService.getQuoteDetail(quoteIdParam!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: isAddingVersion,
  });

  // Fetch existing versions to calculate next version number
  const { data: existingVersions = [] } = useQuery({
    queryKey: ['quote-versions', quoteIdParam],
    queryFn: async () => {
      const result = await quoteService.listVersionsByQuote(quoteIdParam!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: isAddingVersion,
  });

  // Auto-select vendor when adding version
  useEffect(() => {
    if (isAddingVersion && existingQuote?.vendor_id) {
      setSelectedVendorId(existingQuote.vendor_id);
    }
  }, [isAddingVersion, existingQuote]);

  // Auto-match vendor when extraction completes (new quote mode only)
  useEffect(() => {
    if (!isAddingVersion && results?.data?.vendor_name && vendors.length > 0) {
      const extractedName = results.data.vendor_name.toLowerCase().trim();
      const match = vendors.find(
        (v) =>
          v.name.toLowerCase().includes(extractedName) ||
          extractedName.includes(v.name.toLowerCase())
      );
      if (match) setSelectedVendorId(match.id);
    }
  }, [results, vendors, isAddingVersion]);

  const validateAndSetFile = (f: File) => {
    setError(null);
    setResults(null);

    const isPdf = f.type === 'application/pdf';
    const isXlsx =
      f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      f.type === 'application/vnd.ms-excel' ||
      f.name.endsWith('.xlsx') ||
      f.name.endsWith('.xls');

    if (!isPdf && !isXlsx) {
      setError('Please upload a PDF or Excel (.xlsx) file');
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }
    setFile(f);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const handleExtract = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    setProcessingStatus('Reading file...');

    try {
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');

      let body: Record<string, string>;

      if (isExcel) {
        setProcessingStatus('Converting spreadsheet...');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const csvParts: string[] = [];
        for (const sheetName of workbook.SheetNames) {
          csvParts.push(`--- Sheet: ${sheetName} ---`);
          csvParts.push(XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]));
        }
        body = { textContent: csvParts.join('\n') };
      } else {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        body = { fileBase64: base64, mimeType: file.type || 'application/pdf' };
      }

      setProcessingStatus('Extracting data from Quote...');

      const { data, error: fnError } = await quoteService.extractQuote(body);

      if (fnError) throw fnError;

      const extractionResult = data as QuoteExtractionResult;
      setResults(extractionResult);

      // Populate editable fields from extraction
      const d = extractionResult.data;
      setHeaderData({
        well_name: d.well_name || '',
        well_api: d.well_api || '',
        vendor_quote_number: d.vendor_quote_number || '',
        effective_date: d.effective_date || '',
        expiration_date: d.expiration_date || '',
        vendor_notes: d.vendor_notes || '',
      });
      setLineItems(d.line_items || []);

      toast({
        title: 'Extraction Complete',
        description: `Successfully extracted ${d.line_items?.length || 0} line items.`,
      });
    } catch (err) {
      console.error('Extraction error:', err);
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(msg);
      toast({ title: 'Extraction Failed', description: msg, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleExtractEmail = async () => {
    if (!emailText.trim()) return;
    setIsProcessing(true);
    setError(null);
    setProcessingStatus('Extracting data from email conversation...');

    try {
      const body: Record<string, string> = { emailContent: emailText };
      const { data, error: fnError } = await quoteService.extractQuote(body);

      if (fnError) throw fnError;

      const extractionResult = data as QuoteExtractionResult;
      setResults(extractionResult);

      const d = extractionResult.data;
      setHeaderData({
        well_name: d.well_name || '',
        well_api: d.well_api || '',
        vendor_quote_number: d.vendor_quote_number || '',
        effective_date: d.effective_date || '',
        expiration_date: d.expiration_date || '',
        vendor_notes: d.vendor_notes || '',
      });
      setLineItems(d.line_items || []);

      toast({
        title: 'Extraction Complete',
        description: `Successfully extracted ${d.line_items?.length || 0} line items from email.`,
      });
    } catch (err) {
      console.error('Email extraction error:', err);
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(msg);
      toast({ title: 'Extraction Failed', description: msg, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleHeaderChange = (field: string, value: string) => {
    setHeaderData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!results || !profile?.company_id || !user?.id || !selectedVendorId) return;
    setIsSaving(true);

    try {
      const companyId = profile.company_id;
      const context: ServiceContext = { companyId, userId: user.id };

      if (isAddingVersion) {
        // MODE: Adding version to existing quote

        // Validate vendor matches
        if (existingQuote?.vendor_id !== selectedVendorId) {
          throw new Error('Vendor mismatch - cannot add version to a different vendor\'s quote');
        }

        // Calculate next version number
        const versionNumber = Math.max(...existingVersions.map(v => v.version_number), 0) + 1;

        // Get default status for quote_version
        const defaultStatusId = await getDefaultStatusId('quote_version');

        // Create new quote version (DO NOT modify parent quote record)
        const versionResult = await quoteService.createQuoteVersion(
          {
            company_id: companyId,
            quote_id: quoteIdParam!,
            version_number: versionNumber,
            status_id: defaultStatusId,
            vendor_quote_number: headerData.vendor_quote_number || null,
            effective_date: headerData.effective_date || null,
            expiration_date: headerData.expiration_date || null,
            vendor_notes: headerData.vendor_notes || null,
          },
          context
        );
        if (versionResult.error || !versionResult.data) {
          throw new Error(versionResult.error?.message || 'Failed to create quote version');
        }
        const version = versionResult.data;

        // Create quote items
        if (lineItems.length > 0) {
          const items = lineItems.map((item) => ({
            company_id: companyId,
            quote_version_id: version.id,
            sequence: item.sequence,
            vendor_itemcode: item.vendor_itemcode || null,
            vendor_short_description: item.vendor_short_description || null,
            vendor_long_description: item.vendor_long_description || null,
            vendor_quantity: item.vendor_quantity ?? null,
            vendor_unit_price: item.vendor_unit_price ?? null,
            vendor_price: item.vendor_price ?? null,
            vendor_uom: item.vendor_uom || null,
          }));
          const itemsResult = await quoteService.createQuoteItems(items, context);
          if (itemsResult.error) {
            console.error('Quote items error:', itemsResult.error);
          }
        }

        // Store source document (uploaded file or email text)
        const sourceFile = file ?? (emailText.trim()
          ? new File([emailText], 'email-conversation.txt', { type: 'text/plain' })
          : null);
        if (sourceFile) {
          await attachmentService.create(sourceFile, 'quote_version', version.id, context, {
            attachmentRole: 'import source',
          });
        }

        toast({
          title: 'Version Added',
          description: `Version ${versionNumber} has been added.`,
        });
        navigate(`/quotes/${quoteIdParam}`);
      } else {
        // MODE: Creating new quote

        // 1. Create quote
        const quoteResult = await quoteService.createQuote(
          {
            company_id: companyId,
            vendor_id: selectedVendorId,
            well_name: headerData.well_name || null,
            well_api: headerData.well_api || null,
          },
          context
        );
        if (quoteResult.error || !quoteResult.data) {
          throw new Error(quoteResult.error?.message || 'Failed to create quote');
        }
        const quote = quoteResult.data;

        // 2. Get default status for quote_version
        const defaultStatusId = await getDefaultStatusId('quote_version');

        // 3. Create quote version
        const versionResult = await quoteService.createQuoteVersion(
          {
            company_id: companyId,
            quote_id: quote.id,
            version_number: 1,
            status_id: defaultStatusId,
            vendor_quote_number: headerData.vendor_quote_number || null,
            effective_date: headerData.effective_date || null,
            expiration_date: headerData.expiration_date || null,
            vendor_notes: headerData.vendor_notes || null,
          },
          context
        );
        if (versionResult.error || !versionResult.data) {
          throw new Error(versionResult.error?.message || 'Failed to create quote version');
        }
        const version = versionResult.data;

        // 4. Create quote items
        if (lineItems.length > 0) {
          const items = lineItems.map((item) => ({
            company_id: companyId,
            quote_version_id: version.id,
            sequence: item.sequence,
            vendor_itemcode: item.vendor_itemcode || null,
            vendor_short_description: item.vendor_short_description || null,
            vendor_long_description: item.vendor_long_description || null,
            vendor_quantity: item.vendor_quantity ?? null,
            vendor_unit_price: item.vendor_unit_price ?? null,
            vendor_price: item.vendor_price ?? null,
            vendor_uom: item.vendor_uom || null,
          }));
          const itemsResult = await quoteService.createQuoteItems(items, context);
          if (itemsResult.error) {
            console.error('Quote items error:', itemsResult.error);
          }
        }

        // 5. Store source document (uploaded file or email text)
        const sourceFile = file ?? (emailText.trim()
          ? new File([emailText], 'email-conversation.txt', { type: 'text/plain' })
          : null);
        if (sourceFile) {
          await attachmentService.create(sourceFile, 'quote_version', version.id, context, {
            attachmentRole: 'import source',
          });
        }

        toast({
          title: 'Quote Saved',
          description: `Quote ${headerData.vendor_quote_number || quote.id} has been saved.`,
        });
        navigate(`/quotes/${quote.id}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save quote';
      toast({ title: 'Save Failed', description: msg, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setResults(null);
    setError(null);
    setSelectedVendorId(null);
    setEmailText('');
    setHeaderData({
      well_name: '',
      well_api: '',
      vendor_quote_number: '',
      effective_date: '',
      expiration_date: '',
      vendor_notes: '',
    });
    setLineItems([]);
  };

  return (
    <div className="py-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {isAddingVersion ? 'Upload New Quote Version' : 'Upload Quote'}
        </h1>
        <p className="text-muted-foreground mt-1">
          Upload a PDF or Excel quote, or paste in an email conversation describing the quote. Prolarus will automatically extract the supplier pricing.
        </p>
      </div>

      {/* Context banner for adding version */}
      {isAddingVersion && existingQuote && (
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Adding New Version</AlertTitle>
          <AlertDescription>
            You're adding version {existingVersions.length + 1} to quote from{' '}
            <span className="font-semibold">{existingQuote.vendor?.name}</span>
            {existingQuote.well_name && ` for well ${existingQuote.well_name}`}.
          </AlertDescription>
        </Alert>
      )}

      {isAddingVersion && !existingQuote && !isLoadingQuote && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Quote Not Found</AlertTitle>
          <AlertDescription>
            The quote you're trying to add a version to could not be found.
          </AlertDescription>
        </Alert>
      )}

      {!results ? (
        <Tabs defaultValue="pdf-excel" className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
            <TabsTrigger value="pdf-excel" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">PDF or Excel</TabsTrigger>
            <TabsTrigger value="email" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Email Conversation</TabsTrigger>
          </TabsList>
          <TabsContent value="pdf-excel">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upload Quote Document</CardTitle>
            <CardDescription>
              Supports PDF and Excel (.xlsx) files (max 10MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${file ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50'}
                ${isProcessing ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              `}
              onClick={() => !isProcessing && document.getElementById('quote-file-input')?.click()}
            >
              <input
                id="quote-file-input"
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                onChange={handleFileSelect}
                className="hidden"
                disabled={isProcessing}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="w-12 h-12 text-primary" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <p className="font-medium">Drop PDF or Excel file here or click to upload</p>
                  <p className="text-sm text-muted-foreground">Maximum file size: 10MB</p>
                </div>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isProcessing && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="text-muted-foreground">{processingStatus}</span>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              {file && !isProcessing && (
                <Button variant="outline" onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button onClick={handleExtract} disabled={!file || isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Extract Data'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
          </TabsContent>
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Upload Quote from Email Conversation</CardTitle>
                <CardDescription>
                  Copy and paste an email conversation into the text area below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Textarea
                  placeholder="Paste email conversation here..."
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                  className="min-h-[300px]"
                  disabled={isProcessing}
                />

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isProcessing && (
                  <div className="flex items-center justify-center gap-3 py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <span className="text-muted-foreground">{processingStatus}</span>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={handleExtractEmail}
                    disabled={!emailText.trim() || isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Extract Data'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <>
          {/* Full-width toolbar (desktop only) */}
          <div className="hidden md:block space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Confidence:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          results.confidence >= 8
                            ? 'bg-green-500'
                            : results.confidence >= 4
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        } transition-all`}
                        style={{ width: `${(results.confidence / 10) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{results.confidence}/10</span>
                  </div>
                </div>
                {results.debug?.processing_time_ms && (
                  <span className="text-xs text-muted-foreground">
                    {(results.debug.processing_time_ms / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleClear}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
                <Button size="sm" onClick={handleSave} disabled={isSaving || !selectedVendorId}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save Quote
                    </>
                  )}
                </Button>
              </div>
            </div>
            {results.data.explanation && (
              <div className="text-sm text-muted-foreground">
                {explanationExpanded || results.data.explanation.length <= 200 ? (
                  <span>
                    {results.data.explanation}
                    {results.data.explanation.length > 200 && (
                      <button
                        onClick={() => setExplanationExpanded(false)}
                        className="ml-1 text-primary hover:underline"
                      >
                        Show less
                      </button>
                    )}
                  </span>
                ) : (
                  <span>
                    {results.data.explanation.slice(0, 200)}...
                    <button
                      onClick={() => setExplanationExpanded(true)}
                      className="ml-1 text-primary hover:underline"
                    >
                      Show more
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          <ResponsiveSplit
            mobile={
              <div className="space-y-6">
            <QuoteExtractionResults
              results={results}
              headerData={headerData}
              lineItems={lineItems}
              onHeaderChange={handleHeaderChange}
              onLineItemsChange={setLineItems}
              onClear={handleClear}
              onSave={handleSave}
              isSaving={isSaving}
              vendors={vendors}
              selectedVendorId={selectedVendorId}
              onVendorChange={setSelectedVendorId}
              isAddingVersion={isAddingVersion}
              emailText={emailText}
            />
            <UploadQuoteSupportingDocs file={file} isVisible={!!results} />
              </div>
            }
            desktop={
              <>
          <div>
            <QuoteExtractionHeader
              results={results}
              headerData={headerData}
              onHeaderChange={handleHeaderChange}
              vendors={vendors}
              selectedVendorId={selectedVendorId}
              onVendorChange={setSelectedVendorId}
              isAddingVersion={isAddingVersion}
              emailText={emailText}
            />
          </div>

          {file ? (
            <ResizablePanelGroup
              direction="horizontal"
              className="flex min-h-[600px]"
            >
              <ResizablePanel defaultSize={65} minSize={40}>
                <div className="pr-3 h-full overflow-auto">
                  <QuoteExtractionResults
                    results={results}
                    headerData={headerData}
                    lineItems={lineItems}
                    onHeaderChange={handleHeaderChange}
                    onLineItemsChange={setLineItems}
                    onClear={handleClear}
                    onSave={handleSave}
                    isSaving={isSaving}
                    vendors={vendors}
                    selectedVendorId={selectedVendorId}
                    onVendorChange={setSelectedVendorId}
                    isAddingVersion={isAddingVersion}
                    hideToolbar
                    hideHeader
                    emailText={emailText}
                  />
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle />

              <ResizablePanel defaultSize={35} minSize={25}>
                <div className="pl-3 h-full">
                  <UploadQuoteSupportingDocs file={file} isVisible={!!results} />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <div>
              <QuoteExtractionResults
                results={results}
                headerData={headerData}
                lineItems={lineItems}
                onHeaderChange={handleHeaderChange}
                onLineItemsChange={setLineItems}
                onClear={handleClear}
                onSave={handleSave}
                isSaving={isSaving}
                vendors={vendors}
                selectedVendorId={selectedVendorId}
                onVendorChange={setSelectedVendorId}
                isAddingVersion={isAddingVersion}
                hideToolbar
                hideHeader
                emailText={emailText}
              />
            </div>
          )}
              </>
            }
          />
        </>
      )}
    </div>
  );
}
